import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Link2, RefreshCw, TrendingDown, TrendingUp, Radio } from "lucide-react";
import { listGroups, channelInviteLinks } from "@/lib/api";
import { toast } from "sonner";

export default function InviteLinks() {
    const [channels, setChannels] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [linkStats, setLinkStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingLinks, setLoadingLinks] = useState(false);

    const loadChannels = useCallback(async () => {
        try {
            const data = await listGroups();
            const all = data.groups || [];
            setChannels(all.filter(g => g.chat_type === "channel"));
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { loadChannels(); }, [loadChannels]);

    const loadLinks = async (chatId) => {
        setLoadingLinks(true);
        try {
            const stats = await channelInviteLinks(chatId);
            setLinkStats(stats);
        } catch (e) {
            toast.error("فشل تحميل روابط الدعوة");
            setLinkStats(null);
        }
        setLoadingLinks(false);
    };

    const onSelectChannel = (chatId) => {
        setSelectedChat(chatId);
        loadLinks(chatId);
    };

    return (
        <div className="space-y-4 md:space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">روابط الدعوة</h1>
                    <p className="mt-1 text-sm text-muted-foreground">مراقبة الانضمام والمغادرة لكل رابط دعوة</p>
                </div>
                {selectedChat && (
                    <Button variant="outline" size="sm" onClick={() => loadLinks(selectedChat)} className="gap-2">
                        <RefreshCw className="h-4 w-4" /> تحديث
                    </Button>
                )}
            </div>

            {/* Channel Selector */}
            <Card className="bg-card/60 backdrop-blur">
                <CardContent className="p-4">
                    {loading ? <Skeleton className="h-10 w-full" /> : channels.length === 0 ? (
                        <div className="text-center py-8">
                            <Radio className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                            <p className="text-sm text-muted-foreground">لا توجد قنوات بعد</p>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium shrink-0">اختر قناة:</span>
                            <Select value={selectedChat ? String(selectedChat) : ""} onValueChange={(v) => onSelectChannel(Number(v))}>
                                <SelectTrigger className="flex-1" data-testid="invite-links-channel-select">
                                    <SelectValue placeholder="اختر قناة..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {channels.map(ch => (
                                        <SelectItem key={ch.chat_id} value={String(ch.chat_id)}>
                                            {ch.title} {ch.username ? `@${ch.username}` : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Link Stats */}
            {selectedChat && (
                loadingLinks ? (
                    <div className="space-y-3">
                        <Skeleton className="h-24" />
                        <Skeleton className="h-48" />
                    </div>
                ) : linkStats ? (
                    <div className="space-y-4">
                        {/* Summary KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Card className="bg-card/60 backdrop-blur ring-1 ring-cyan-500/15">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-muted-foreground">إجمالي الاستخدام</p>
                                    <p className="text-2xl font-bold mt-1" dir="ltr">{(linkStats.total_usage || 0).toLocaleString()}</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-card/60 backdrop-blur ring-1 ring-emerald-500/15">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-muted-foreground">انضمام اليوم</p>
                                    <p className="text-2xl font-bold mt-1 text-emerald-400" dir="ltr">+{linkStats.today_total_joins || 0}</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-card/60 backdrop-blur ring-1 ring-rose-500/15">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-muted-foreground">مغادرة اليوم</p>
                                    <p className="text-2xl font-bold mt-1 text-rose-400" dir="ltr">-{linkStats.today_total_leaves || 0}</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-card/60 backdrop-blur ring-1 ring-cyan-500/15">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-muted-foreground">عدد الروابط</p>
                                    <p className="text-2xl font-bold mt-1" dir="ltr">{(linkStats.current_links || []).length}</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Today's Events by Link */}
                        {(linkStats.today_events_by_link || []).length > 0 && (
                            <Card className="bg-card/60 backdrop-blur">
                                <CardHeader><CardTitle className="text-base">إحصائيات اليوم حسب الرابط</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {linkStats.today_events_by_link.map((item, i) => (
                                            <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 bg-card/30 p-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">{item.title || item.link.split("/").pop()}</p>
                                                    <p className="text-[11px] text-muted-foreground font-mono truncate" dir="ltr">{item.link}</p>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs shrink-0">
                                                    <span className="flex items-center gap-1 text-emerald-400"><TrendingUp className="h-3 w-3" /> +{item.joins}</span>
                                                    <span className="flex items-center gap-1 text-rose-400"><TrendingDown className="h-3 w-3" /> -{item.leaves}</span>
                                                    <Badge variant="outline" className={item.net >= 0 ? "text-emerald-300 bg-emerald-500/10" : "text-rose-300 bg-rose-500/10"}>
                                                        {item.net >= 0 ? "+" : ""}{item.net}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Current Links */}
                        {(linkStats.current_links || []).length > 0 && (
                            <Card className="bg-card/60 backdrop-blur">
                                <CardHeader><CardTitle className="text-base">الروابط الحالية</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {linkStats.current_links.map((lnk, i) => (
                                            <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 bg-card/30 p-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-medium truncate">{lnk.title || (lnk.permanent ? "الرابط الدائم" : lnk.link.split("/").pop())}</p>
                                                        {lnk.permanent && <Badge variant="outline" className="text-[10px]">دائم</Badge>}
                                                    </div>
                                                    <p className="text-[11px] text-muted-foreground font-mono truncate" dir="ltr">{lnk.link}</p>
                                                </div>
                                                <div className="text-left shrink-0">
                                                    <p className="text-lg font-bold" dir="ltr">{(lnk.usage || 0).toLocaleString()}</p>
                                                    <p className="text-[10px] text-muted-foreground">استخدام</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Last 24h Drops */}
                        {(linkStats.last_24h_drops || []).length > 0 && (
                            <Card className="bg-card/60 backdrop-blur ring-1 ring-rose-500/10">
                                <CardHeader><CardTitle className="text-base text-rose-300">آخر المغادرات (24 ساعة)</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {linkStats.last_24h_drops.slice(0, 20).map((drop, i) => (
                                            <div key={i} className="flex items-center justify-between rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium">{drop.title || drop.link.split("/").pop()}</p>
                                                    <p className="text-[11px] text-muted-foreground">{drop.ts?.substring(0, 16).replace("T", " ")}</p>
                                                </div>
                                                <div className="text-rose-400 font-bold text-sm" dir="ltr">-{drop.delta}</div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : (
                    <Card className="bg-card/40">
                        <CardContent className="py-12 text-center text-muted-foreground">
                            <Link2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p>لا توجد بيانات روابط لهذه القناة</p>
                        </CardContent>
                    </Card>
                )
            )}
        </div>
    );
}
